import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, FormControl, InputLabel, Select, MenuItem, Box, Typography,
  Chip, InputAdornment, IconButton, Divider, Alert, CircularProgress, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { fileService, partService, truckService } from '../api/api';
import { serializeInvoiceUrls } from './truckFormConfig';
import { getVehicleCategoryId } from '../utils/vehicleCategories';
import { currentPartPlacement } from '../utils/partLocation.mjs';

const RESIDENCE_MODES = {
  SHELF: 'shelf',
  SOLVENT_SHED: 'solventShed',
  SHOP: 'shop',
  PARTS_TRUCK: 'partsTruck',
  VEHICLE: 'vehicle',
};

const STORAGE_TRANSFERS = {
  SHOP_TO_SOLVENT_SHED: 'shopToSolventShed',
  SOLVENT_SHED_TO_SHOP: 'solventShedToShop',
};

const storageTransferOptions = [
  { value: STORAGE_TRANSFERS.SHOP_TO_SOLVENT_SHED, label: 'Shop → Solvent Shed', from: 'Shop', to: 'Solvent Shed' },
  { value: STORAGE_TRANSFERS.SOLVENT_SHED_TO_SHOP, label: 'Solvent Shed → Shop', from: 'Solvent Shed', to: 'Shop' },
];
const inventoryStorageLocations = ['Shelf', 'Solvent Shed', 'Shop'];

function assignmentValue(truckId, location, fallback = '') {
  if (truckId) return `truck:${truckId}`;
  if (location) return `location:${location}`;
  return fallback;
}

const initialFormState = {
  sku: '',
  make: '',
  model: '',
  year: new Date().getFullYear(),
  part_type: '',
  part_number_oem: '',
  biomed_part_number_sku: '',
  serial_number: '',
  cross_reference: '',
  quantity: 1,
  unit_came_off_from: '',
  unit_came_off_from_location: '',
  unit_intended_for: '',
  unit_intended_for_location: '',
  used_new: true,
  condition_scale: 5,
  notes: '',
  condition_notes: '',
  condition_of_storage: '',
  recommended_storage: '',
  location: '',
  date_purchased: '',
  purchase_price: '',
  current_value: '',
  receipt_number: '',
  photo_url: '',
  invoice_url: ''
};

export default function AddPartDialog({
  open,
  onClose,
  onPartAdded,
  defaultUnitCameOffFrom = '',
  defaultUnitIntendedFor = '',
  hideVehicleAssignment = false,
}) {
  const [formData, setFormData] = useState(initialFormState);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [invoicePreviews, setInvoicePreviews] = useState([]);
  const [residenceMode, setResidenceMode] = useState(RESIDENCE_MODES.SHELF);
  const [storageTransfer, setStorageTransfer] = useState('');
  const [storageBayLocation, setStorageBayLocation] = useState('');

  useEffect(() => {
    if (open) {
      fetchTrucks();
      setFormData({
        ...initialFormState,
        unit_came_off_from: defaultUnitCameOffFrom || '',
        unit_intended_for: defaultUnitIntendedFor || '',
        unit_came_off_from_location: defaultUnitCameOffFrom || defaultUnitIntendedFor ? '' : 'Shelf',
      });
      setResidenceMode(defaultUnitIntendedFor ? RESIDENCE_MODES.VEHICLE : RESIDENCE_MODES.SHELF);
      setStorageTransfer('');
      setStorageBayLocation('');
      setError('');
      setInvoiceFiles([]);
      setInvoicePreviews([]);
      setPhotoFiles([]);
      setPhotoPreviews([]);
    }
  }, [open, defaultUnitCameOffFrom, defaultUnitIntendedFor]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFile = files.find((file) => !allowedTypes.includes(file.type));
    if (invalidFile) {
      setError('Invalid image type. Only JPEG, PNG, GIF, and WEBP are allowed.');
      e.target.value = '';
      return;
    }

    const oversizedFile = files.find((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFile) {
      setError('One or more photos are too large. Maximum size is 10MB each.');
      e.target.value = '';
      return;
    }

    setPhotoFiles((prev) => [...prev, ...files].slice(0, 35));
    setPhotoPreviews((prev) => [
      ...prev,
      ...files.map((file) => URL.createObjectURL(file)),
    ].slice(0, 35));
    setFormData((prev) => ({ ...prev, photo_url: '' }));
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotoFiles((prev) => prev.filter((_, idx) => idx !== index));
    setPhotoPreviews((prev) => prev.filter((_, idx) => idx !== index));
    setFormData((prev) => ({ ...prev, photo_url: '' }));
  };

  const handleInvoiceUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (files.some((file) => !allowedTypes.includes(file.type))) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and PDF are allowed.');
      return;
    }

    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setInvoiceFiles((prev) => [...prev, ...files]);
    setInvoicePreviews((prev) => [...prev, ...files.map((file) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : 'pdf'))]);
    setFormData((prev) => ({ ...prev, invoice_url: '' }));
    e.target.value = '';
  };

  const removeInvoice = (index) => {
    setInvoicePreviews((prev) => {
      const preview = prev[index];
      if (preview && preview !== 'pdf') {
        try { URL.revokeObjectURL(preview); } catch { /* noop */ }
      }
      return prev.filter((_, i) => i !== index);
    });
    setInvoiceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  async function fetchTrucks() {
    try {
      const res = await truckService.getAll();
      setTrucks(res.data || []);
    } catch (err) {
      console.error('Error fetching trucks:', err);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'location') setStorageBayLocation(value);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getTruckLabel = (truck) => [
    truck.name || `Vehicle ${truck.id}`,
    truck.license_plate ? `Plate: ${truck.license_plate}` : null,
    [truck.year, truck.make, truck.model].filter(Boolean).join(' '),
  ].filter(Boolean).join(' | ');

  const partsTruckOptions = useMemo(
    () => trucks.filter((truck) => getVehicleCategoryId(truck) === 'partsTruck'),
    [trucks]
  );
  const vehicleOptions = useMemo(
    () => trucks.filter((truck) => getVehicleCategoryId(truck) !== 'partsTruck'),
    [trucks]
  );

  useEffect(() => {
    if (!open || hideVehicleAssignment) return;
    if (residenceMode === RESIDENCE_MODES.PARTS_TRUCK && !formData.unit_intended_for && !formData.unit_came_off_from && partsTruckOptions.length > 0) {
      setFormData((prev) => ({
        ...prev,
        unit_intended_for: partsTruckOptions[0].id,
      }));
    }
  }, [open, hideVehicleAssignment, residenceMode, formData.unit_intended_for, formData.unit_came_off_from, partsTruckOptions]);

  const handleResidenceModeChange = (mode) => {
    setResidenceMode(mode);
    setStorageTransfer('');
    setStorageBayLocation('');
    setError('');

    if (mode === RESIDENCE_MODES.SHELF) {
      setFormData((prev) => ({
        ...prev,
        unit_came_off_from: '',
        unit_came_off_from_location: 'Shelf',
        unit_intended_for: '',
        unit_intended_for_location: '',
      }));
      return;
    }

    if (mode === RESIDENCE_MODES.SOLVENT_SHED || mode === RESIDENCE_MODES.SHOP) {
      setFormData((prev) => ({
        ...prev,
        unit_came_off_from: '',
        unit_came_off_from_location: '',
        unit_intended_for: '',
        unit_intended_for_location: mode === RESIDENCE_MODES.SOLVENT_SHED ? 'Solvent Shed' : 'Shop',
      }));
      return;
    }

    if (mode === RESIDENCE_MODES.PARTS_TRUCK) {
      const currentPartsTruckId = partsTruckOptions.some((truck) => String(truck.id) === String(formData.unit_intended_for))
        ? formData.unit_intended_for
        : '';
      const selectedTruckId = currentPartsTruckId || partsTruckOptions[0]?.id || '';
      setFormData((prev) => ({
        ...prev,
        unit_came_off_from: '',
        unit_came_off_from_location: '',
        unit_intended_for: selectedTruckId,
        unit_intended_for_location: '',
      }));
      return;
    }

    const currentVehicleId = vehicleOptions.some((truck) => String(truck.id) === String(formData.unit_intended_for))
      ? formData.unit_intended_for
      : '';
    setFormData((prev) => ({
      ...prev,
      unit_came_off_from: '',
      unit_came_off_from_location: '',
      unit_intended_for: currentVehicleId,
      unit_intended_for_location: '',
    }));
  };

  const handleResidenceVehicleChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      unit_came_off_from: '',
      unit_came_off_from_location: '',
      unit_intended_for: value,
      unit_intended_for_location: '',
    }));
  };

  const handleAssignmentChange = (side, value) => {
    setStorageTransfer('');
    setStorageBayLocation('');
    const isTruck = String(value).startsWith('truck:');
    const isLocation = String(value).startsWith('location:');
    const location = isLocation ? String(value).slice(9) : '';
    const truckId = isTruck ? String(value).slice(6) : '';
    const nextData = { ...formData, [`unit_${side}`]: truckId, [`unit_${side}_location`]: location };
    setFormData(nextData);
    const placement = currentPartPlacement(nextData);
    const selectedTruck = trucks.find((truck) => Number(truck.id) === placement.truckId);
    if (placement.truckId) {
      setResidenceMode(getVehicleCategoryId(selectedTruck) === 'partsTruck' ? RESIDENCE_MODES.PARTS_TRUCK : RESIDENCE_MODES.VEHICLE);
    } else {
      setResidenceMode(placement.storageArea === 'Shop' ? RESIDENCE_MODES.SHOP : placement.storageArea === 'Solvent Shed' ? RESIDENCE_MODES.SOLVENT_SHED : RESIDENCE_MODES.SHELF);
    }
  };

  const handleStorageTransferChange = (value) => {
    setStorageTransfer(value);
    setStorageBayLocation(formData.location || '');
    setError('');
    const transfer = storageTransferOptions.find((option) => option.value === value);
    if (!transfer) {
      const currentArea = residenceMode === RESIDENCE_MODES.SHOP ? 'Shop' : 'Solvent Shed';
      setFormData((current) => ({
        ...current,
        unit_came_off_from: '',
        unit_came_off_from_location: currentArea,
        unit_intended_for: '',
        unit_intended_for_location: '',
      }));
      return;
    }

    setResidenceMode(transfer.to === 'Shop' ? RESIDENCE_MODES.SHOP : RESIDENCE_MODES.SOLVENT_SHED);
    setFormData((current) => ({
      ...current,
      unit_came_off_from: '',
      unit_came_off_from_location: transfer.from,
      unit_intended_for: '',
      unit_intended_for_location: transfer.to,
    }));
  };

  const handleStorageBayLocationChange = (value) => {
    setStorageBayLocation(value);
    const transfer = storageTransferOptions.find((option) => option.value === storageTransfer);
    if (!transfer) return;
    setFormData((current) => ({ ...current, location: value }));
  };

  const handleSubmit = async () => {
    if (!formData.sku || !formData.part_type || !formData.location) {
      setError('Please fill in required fields: SKU, Part Type, and Location');
      return;
    }
    if (storageTransfer && !storageBayLocation.trim()) {
      setError('Please enter the exact destination location, for example Bay-01-Z083.');
      return;
    }
    if (!hideVehicleAssignment && residenceMode === RESIDENCE_MODES.PARTS_TRUCK && !currentPartPlacement(formData).truckId) {
      setError('Please select which Parts Truck this part resides on.');
      return;
    }
    if (!hideVehicleAssignment && residenceMode === RESIDENCE_MODES.VEHICLE && !currentPartPlacement(formData).truckId) {
      setError('Please select which vehicle this part currently resides in.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const invoiceUrls = [];

      if (invoiceFiles.length) {
        setUploading(true);
        for (const invoiceFile of invoiceFiles) {
          const uploadRes = await fileService.uploadInvoice(invoiceFile);
          if (uploadRes.data.invoicePath) invoiceUrls.push(uploadRes.data.invoicePath);
        }
      }

      const partData = {
        ...formData,
        year: parseInt(formData.year) || new Date().getFullYear(),
        quantity: Math.max(0, parseInt(formData.quantity, 10) || 0),
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        current_value: formData.current_value ? parseFloat(formData.current_value) : null,
        date_purchased: formData.date_purchased || null,
        unit_came_off_from: formData.unit_came_off_from ? parseInt(formData.unit_came_off_from) : null,
        unit_came_off_from_location: formData.unit_came_off_from_location || null,
        unit_intended_for: formData.unit_intended_for ? parseInt(formData.unit_intended_for) : null,
        unit_intended_for_location: formData.unit_intended_for_location || null,
        photo_url: null,
        invoice_url: serializeInvoiceUrls(invoiceUrls),
      };

      const response = await partService.create(partData);
      let finalPart = response.data;

      if (photoFiles.length) {
        setPhotoUploading(true);
        await partService.uploadPhotos(response.data.id, photoFiles);
        const refreshed = await partService.getById(response.data.id);
        finalPart = refreshed.data;
        setPhotoFiles([]);
        setPhotoPreviews([]);
      }
      
      if (onPartAdded) {
        onPartAdded(finalPart);
      }
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      onClose();
    } catch (err) {
      console.error('Error creating part:', err);
      setError('Failed to create part. Please try again.');
    } finally {
      setLoading(false);
      setUploading(false);
      setPhotoUploading(false);
    }
  };

  const conditionLevels = [
    { value: 1, label: 'Poor', color: '#d32f2f', bgColor: '#ffebee', description: 'Major damage, needs repair or replacement' },
    { value: 2, label: 'Fair', color: '#f57c00', bgColor: '#fff3e0', description: 'Visible wear, functional but limited life' },
    { value: 3, label: 'Good', color: '#fbc02d', bgColor: '#fffde7', description: 'Normal wear, fully functional' },
    { value: 4, label: 'Very Good', color: '#689f38', bgColor: '#f1f8e9', description: 'Minimal wear, like new condition' },
    { value: 5, label: 'Excellent', color: '#2e7d32', bgColor: '#e8f5e9', description: 'Brand new or pristine condition' },
  ];

  const currentCondition = conditionLevels.find(c => c.value === formData.condition_scale) || conditionLevels[4];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BuildIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Add New Part</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#001f3f', mb: 2 }}>
          Basic Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="SKU *"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              placeholder="e.g., BM-ENG-001"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Part Type *"
              name="part_type"
              value={formData.part_type}
              onChange={handleChange}
              placeholder="e.g., Engine Oil Filter"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Location *"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., B20-32-07-B1"
              size="small"
              helperText="Exact bay or bin within the selected storage area."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Make"
              name="make"
              value={formData.make}
              onChange={handleChange}
              placeholder="e.g., Volvo"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              placeholder="e.g., FH16"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Year"
              name="year"
              type="number"
              value={formData.year}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              size="small"
              inputProps={{ min: 0, step: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Inventory2Icon fontSize="small" /></InputAdornment>,
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#001f3f', mb: 2 }}>
          Part Numbers & References
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="OEM Part Number"
              name="part_number_oem"
              value={formData.part_number_oem}
              onChange={handleChange}
              placeholder="e.g., VOL-85108176"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Biomed Part Number"
              name="biomed_part_number_sku"
              value={formData.biomed_part_number_sku}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Serial Number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              placeholder="e.g., 2009-H1-P1-Alternator"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cross Reference"
              name="cross_reference"
              value={formData.cross_reference}
              onChange={handleChange}
              placeholder="e.g., Mann HU12140X, Fram CH10358"
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#001f3f', mb: 2 }}>
          Condition & Status
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="used_new"
                value={formData.used_new}
                onChange={(e) => setFormData(prev => ({ ...prev, used_new: e.target.value }))}
                label="Status"
              >
                <MenuItem value={true}>
                  <Chip label="New" size="small" sx={{ bgcolor: '#c8e6c9', color: '#2e7d32' }} />
                </MenuItem>
                <MenuItem value={false}>
                  <Chip label="Used" size="small" sx={{ bgcolor: '#ffccbc', color: '#d84315' }} />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: currentCondition.bgColor,
              border: `2px solid ${currentCondition.color}`,
              textAlign: 'center'
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: currentCondition.color }}>
                {currentCondition.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {currentCondition.description}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
              Select Part Condition (click a rating):
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {conditionLevels.map((level) => (
                <Box
                  key={level.value}
                  onClick={() => setFormData(prev => ({ ...prev, condition_scale: level.value }))}
                  sx={{
                    flex: { xs: '1 1 45%', sm: '1 1 18%' },
                    minWidth: { xs: 100, sm: 80 },
                    p: 1.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: formData.condition_scale === level.value 
                      ? `3px solid ${level.color}` 
                      : '2px solid #e0e0e0',
                    bgcolor: formData.condition_scale === level.value 
                      ? level.bgColor 
                      : '#fafafa',
                    boxShadow: formData.condition_scale === level.value 
                      ? `0 0 8px ${level.color}40` 
                      : 'none',
                    transform: formData.condition_scale === level.value 
                      ? 'scale(1.02)' 
                      : 'scale(1)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: level.bgColor,
                      borderColor: level.color,
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: formData.condition_scale === level.value ? level.color : '#757575',
                      fontSize: { xs: '1rem', sm: '1.1rem' }
                    }}
                  >
                    {level.value}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      color: formData.condition_scale === level.value ? level.color : '#9e9e9e',
                      fontWeight: formData.condition_scale === level.value ? 600 : 400,
                      fontSize: { xs: '0.65rem', sm: '0.7rem' }
                    }}
                  >
                    {level.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Part Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="General notes about this part..."
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Condition Notes"
              name="condition_notes"
              value={formData.condition_notes}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="Describe the condition of this part..."
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#001f3f', mb: 2 }}>
          Storage Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Current Storage Condition"
              name="condition_of_storage"
              value={formData.condition_of_storage}
              onChange={handleChange}
              placeholder="e.g., Climate controlled warehouse"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Recommended Storage"
              name="recommended_storage"
              value={formData.recommended_storage}
              onChange={handleChange}
              placeholder="e.g., Keep dry, room temperature"
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {!hideVehicleAssignment && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #bbdefb', backgroundColor: '#f8fbff' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#001f3f', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon /> Where is this part right now?
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Choose the current residence so inventory quantities and transfers use the correct storage location.
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[
                {
                  value: RESIDENCE_MODES.SHELF,
                  title: 'Adding a part on shelf?',
                  detail: 'Counts as shelf inventory and is available for future vehicle assignment.',
                  icon: <Inventory2Icon />,
                },
                {
                  value: RESIDENCE_MODES.SOLVENT_SHED,
                  title: 'Adding a part in the Solvent Shed?',
                  detail: 'Tracks this item as available Solvent Shed inventory.',
                  icon: <Inventory2Icon />,
                },
                {
                  value: RESIDENCE_MODES.SHOP,
                  title: 'Adding a part in the Shop?',
                  detail: 'Tracks this item as available Shop inventory.',
                  icon: <BuildIcon />,
                },
                {
                  value: RESIDENCE_MODES.PARTS_TRUCK,
                  title: 'Adding a part on a parts truck?',
                  detail: 'Counts as inventory because it is stored on a parts truck.',
                  icon: <LocalShippingIcon />,
                },
                {
                  value: RESIDENCE_MODES.VEHICLE,
                  title: 'Adding a part in other vehicles?',
                  detail: 'Tracks this part as already installed or residing in a specific vehicle.',
                  icon: <BuildIcon />,
                },
              ].map((option) => {
                const selected = residenceMode === option.value;
                return (
                  <Grid item xs={12} md={4} key={option.value}>
                    <Paper
                      onClick={() => handleResidenceModeChange(option.value)}
                      sx={{
                        height: '100%',
                        p: 1.75,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: selected ? '2px solid #1976d2' : '1px solid #d7e3f4',
                        backgroundColor: selected ? '#e3f2fd' : '#fff',
                        boxShadow: selected ? '0 8px 18px rgba(25, 118, 210, 0.16)' : 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#1976d2',
                          backgroundColor: selected ? '#e3f2fd' : '#f6fbff',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          color: selected ? 'white' : '#1976d2',
                          backgroundColor: selected ? '#1976d2' : '#e3f2fd',
                          flexShrink: 0,
                        }}>
                          {option.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#001f3f', lineHeight: 1.25 }}>
                            {option.title}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            {option.detail}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5, borderColor: '#c4b5fd', bgcolor: '#faf8ff' }}>
              <Typography sx={{ fontWeight: 900, color: '#4c1d95', mb: 0.5 }}>
                Part moved?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Use this when transferring inventory directly between the Shop and Solvent Shed.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: storageTransfer ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' }, gap: 1.5, width: '100%' }}>
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Storage transfer</InputLabel>
                    <Select
                      value={storageTransfer}
                      label="Storage transfer"
                      onChange={(event) => handleStorageTransferChange(event.target.value)}
                    >
                      <MenuItem value="">Not moved between storage areas</MenuItem>
                      {storageTransferOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {storageTransfer && (
                  <Box sx={{ minWidth: 0, width: '100%' }}>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      label="Exact destination location"
                      value={storageBayLocation}
                      onChange={(event) => handleStorageBayLocationChange(event.target.value)}
                      placeholder="Bay-01-Z083"
                      helperText={`Exact bay or bin within ${storageTransferOptions.find((option) => option.value === storageTransfer)?.to}; saved as entered.`}
                    />
                  </Box>
                )}
              </Box>
            </Paper>

            {!storageTransfer && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5, borderColor: '#bfdbfe', bgcolor: '#f8fbff' }}>
                <Typography sx={{ fontWeight: 900, color: '#0f4c81', mb: 0.5 }}>
                  Unit movement / assignment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Use both fields when a part moved from one vehicle or inventory area to another.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unit Came Off From</InputLabel>
                      <Select
                        value={assignmentValue(formData.unit_came_off_from, formData.unit_came_off_from_location, 'location:Shelf')}
                        label="Unit Came Off From"
                        onChange={(event) => handleAssignmentChange('came_off_from', event.target.value)}
                      >
                        {inventoryStorageLocations.map((location) => (
                          <MenuItem key={location} value={`location:${location}`}>{location}</MenuItem>
                        ))}
                        {trucks.map((truck) => (
                          <MenuItem key={truck.id} value={`truck:${truck.id}`}>{getTruckLabel(truck)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unit Intended For</InputLabel>
                      <Select
                        value={assignmentValue(formData.unit_intended_for, formData.unit_intended_for_location)}
                        label="Unit Intended For"
                        onChange={(event) => handleAssignmentChange('intended_for', event.target.value)}
                      >
                        <MenuItem value="">No destination vehicle</MenuItem>
                        {inventoryStorageLocations.map((location) => (
                          <MenuItem key={location} value={`location:${location}`}>{location}</MenuItem>
                        ))}
                        {trucks.map((truck) => (
                          <MenuItem key={truck.id} value={`truck:${truck.id}`}>{getTruckLabel(truck)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {residenceMode === RESIDENCE_MODES.SHELF && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                This part will be saved as Shelf stock. Inventory Management will include its quantity.
              </Alert>
            )}

            {residenceMode === RESIDENCE_MODES.SOLVENT_SHED && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                This part will be saved as Solvent Shed inventory and will be available in service logs.
              </Alert>
            )}

            {residenceMode === RESIDENCE_MODES.SHOP && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                This part will be saved as Shop inventory and will be available in service logs.
              </Alert>
            )}

            {residenceMode === RESIDENCE_MODES.PARTS_TRUCK && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Parts Truck *</InputLabel>
                    <Select
                      name="unit_intended_for"
                      value={formData.unit_intended_for}
                      onChange={handleResidenceVehicleChange}
                      label="Parts Truck *"
                    >
                      {partsTruckOptions.map((truck) => (
                        <MenuItem key={truck.id} value={truck.id}>
                          {getTruckLabel(truck)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  {partsTruckOptions.length === 0 ? (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      No vehicle is categorized as Parts Truck yet.
                    </Alert>
                  ) : (
                    <Alert severity="success" sx={{ py: 0.5 }}>
                      Quantity will count with Shelf inventory.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            )}

            {residenceMode === RESIDENCE_MODES.VEHICLE && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vehicle *</InputLabel>
                    <Select
                      name="unit_intended_for"
                      value={formData.unit_intended_for}
                      onChange={handleResidenceVehicleChange}
                      label="Vehicle *"
                    >
                      {vehicleOptions.map((truck) => (
                        <MenuItem key={truck.id} value={truck.id}>
                          {getTruckLabel(truck)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    This will not count as storage inventory because it is assigned to a fleet vehicle.
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#001f3f', mb: 2 }}>
          Financial Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <TextField
                fullWidth
                label="Date Purchased (Optional)"
                name="date_purchased"
                type="date"
                value={formData.date_purchased || ''}
                onChange={handleChange}
                size="small"
                helperText="Leave blank if the purchase date is unknown."
                InputLabelProps={{ shrink: true }}
              />
              {formData.date_purchased && (
                <Button size="small" color="inherit" onClick={() => setFormData((prev) => ({ ...prev, date_purchased: '' }))} sx={{ mt: 0.25 }}>
                  Clear date
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Purchase Price"
              name="purchase_price"
              type="number"
              value={formData.purchase_price}
              onChange={handleChange}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Current Value"
              name="current_value"
              type="number"
              value={formData.current_value}
              onChange={handleChange}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Receipt Number"
              name="receipt_number"
              value={formData.receipt_number}
              onChange={handleChange}
              placeholder="e.g., RCP-2024-1101"
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon /> Part Photo (Optional)
          </Typography>

          {photoFiles.length === 0 ? (
            <Box
              component="label"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2.5, sm: 3 },
                border: '2px dashed #1976d2',
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: '#e3f2fd',
                '&:hover': { backgroundColor: '#bbdefb' }
              }}
            >
              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#1976d2', mb: 1 }} />
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                Tap to upload part photos (JPEG, PNG, GIF, WEBP — Max 10MB each)
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              backgroundColor: '#fff',
              borderRadius: 2,
              border: '1px solid #bbdefb'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
                {photoPreviews.map((preview, index) => (
                  <Box key={preview} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={preview}
                      alt={`Part preview ${index + 1}`}
                      sx={{
                        width: { xs: 104, sm: 126 },
                        height: { xs: 84, sm: 96 },
                        objectFit: 'contain',
                        backgroundColor: '#f8fbff',
                        borderRadius: 1.5,
                        border: '1px solid #d7e3f4',
                      }}
                    />
                    <IconButton
                      onClick={() => removePhoto(index)}
                      color="error"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'white',
                        boxShadow: 1,
                        '&:hover': { backgroundColor: '#ffebee' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!photoUploading && (
                  <Box component="label" sx={{ cursor: 'pointer' }}>
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                    <Button variant="outlined" size="small" component="span" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2 }}>
                      Add More
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {photoUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Uploading photo…</Typography>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> Invoice Attachment (Optional)
          </Typography>

          <Box
            component="label"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2.5, sm: 3 },
              border: '2px dashed #4caf50',
              borderRadius: 2,
              cursor: 'pointer',
              backgroundColor: '#f1f8e9',
              '&:hover': { backgroundColor: '#dcedc8' }
            }}
          >
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf"
              onChange={handleInvoiceUpload}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#2e7d32', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', textAlign: 'center' }}>
              Tap to add invoice attachments (JPEG, PNG, GIF, PDF — Max 10MB each)
            </Typography>
          </Box>

          {invoiceFiles.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {invoiceFiles.map((file, index) => (
                <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                  {invoicePreviews[index] && invoicePreviews[index] !== 'pdf' ? (
                    <Box component="img" src={invoicePreviews[index]} alt="Invoice preview" sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />
                  ) : (
                    <ReceiptIcon sx={{ color: '#d32f2f', fontSize: 30 }} />
                  )}
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 800 }}>{file.name}</Typography>
                  <IconButton onClick={() => removeInvoice(index)} color="error" size="small" sx={{ backgroundColor: '#ffebee' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {uploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Uploading invoice…</Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || uploading || photoUploading}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #003d7a 0%, #001f3f 100%)',
            }
          }}
        >
          {photoUploading || uploading ? 'Uploading...' : loading ? 'Adding...' : 'Add Part'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
